import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loginSchema, type LoginFormInput, type LoginFormValues } from '@emazy/shared-validation';

import { FormErrorSummary, PasswordField, SubmitButton } from '@/features/auth/components';
import { useAuth } from '@/features/auth/context';
import { mockDevelopmentAccounts } from '@/features/auth/services/mockAuthService';
import {
  getAuthErrorMessage,
  getSafeRedirectTarget,
  normalizeAuthError,
} from '@/features/auth/utils';
import { buildForgotPasswordRoute, buildVerifyEmailRoute, routes } from '@/config/routes';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';
import { env } from '@/config/env';

export function LoginPage() {
  useDocumentMetadata('Sign In');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [authError, setAuthError] = useState<ReturnType<typeof normalizeAuthError> | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInput, unknown, LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: searchParams.get('email') ?? '', password: '', rememberMe: false },
  });
  const email = useWatch({ control, name: 'email', defaultValue: searchParams.get('email') ?? '' });

  async function submit(values: LoginFormValues) {
    setAuthError(null);
    try {
      await login(values);
      navigate(getSafeRedirectTarget(searchParams.get('redirect')), { replace: true });
    } catch (error) {
      setAuthError(normalizeAuthError(error));
    }
  }

  return (
    <div className="auth-page">
      <p className="eyebrow">Welcome back</p>
      <h1>Sign in to Emazy</h1>
      <p className="auth-page__intro">
        Return to the account foundation for saved designs, orders, and future reorders.
      </p>
      <FormErrorSummary message={authError ? getAuthErrorMessage(authError) : undefined}>
        {authError?.code === 'email-not-verified' && (
          <Link to={buildVerifyEmailRoute({ email })}>Verify your email</Link>
        )}
        {authError?.code === 'password-reset-required' && (
          <Link to={buildForgotPasswordRoute({ email })}>Reset your password</Link>
        )}
      </FormErrorSummary>
      <form className="auth-form" noValidate onSubmit={handleSubmit(submit)}>
        <label className="auth-field" htmlFor="login-email">
          <span>Email</span>
          <input
            id="login-email"
            className="p-inputtext p-component"
            type="email"
            autoComplete="email"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            {...register('email')}
          />
          {errors.email && (
            <small id="login-email-error" role="alert">
              {errors.email.message}
            </small>
          )}
        </label>
        <PasswordField
          id="login-password"
          label="Password"
          autoComplete="current-password"
          registration={register('password')}
          error={errors.password?.message}
        />
        <div className="auth-form__options">
          <label className="auth-checkbox">
            <input type="checkbox" {...register('rememberMe')} />
            <span>Remember me on this device</span>
          </label>
          <Link to={buildForgotPasswordRoute({ email })}>Forgot password?</Link>
        </div>
        <SubmitButton label="Sign in" loading={isSubmitting} />
      </form>
      <p className="auth-page__alternate">
        New to Emazy? <Link to={routes.register}>Create an account</Link>
      </p>
      {import.meta.env.DEV && env.authMode === 'mock' && (
        <aside className="mock-account-panel">
          <strong>Local mock accounts</strong>
          <p>Fill a development account without submitting.</p>
          <div>
            {mockDevelopmentAccounts.map((account) => (
              <button
                type="button"
                key={account.email}
                onClick={() => {
                  setValue('email', account.email);
                  setValue('password', account.password);
                }}
              >
                {account.label}
              </button>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}
